import { useMemo, useState, useSyncExternalStore } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { RootState } from "@Reducers/index";
import { setShowPortalChat } from "../../../reducers/portalChat";
import { usePortalChatCore } from "../core/usePortalChatCore";
import type { PortalChatPlatform } from "../core/platform";
import { useChatListModel } from "../core/useChatListModel";
import { PANEL_ANCHOR_GAP } from "../portalChat.constants";
import { PortalChatContext } from "./context";
import { ChatListModelContext } from "./listModelContext";
import ChatListPane from "./ChatListPane";
import ChatThreadPane from "./ChatThreadPane";
import { ForwardDialog, ReceiptsDialog, ReactionsDialog, EditMessageDialog } from "./ChatDialogs";

/**
 * Web platform adapter for the shared chat core: per-tab persistence via sessionStorage, tab
 * visibility via the document, cookie-authenticated socket (no extra connect options), and the
 * PRD §4.1 reopen-on-last-conversation behaviour.
 */
const webPlatform: PortalChatPlatform = {
  storage: {
    getItem: (key) => {
      try {
        return sessionStorage.getItem(key);
      } catch {
        return null;
      }
    },
    setItem: (key, value) => {
      try {
        sessionStorage.setItem(key, value);
      } catch {
        // sessionStorage may be unavailable; persistence is best-effort.
      }
    },
  },
  isAppVisible: () => document.visibilityState === "visible",
  subscribeAppVisibility: (onChange) => {
    document.addEventListener("visibilitychange", onChange);
    return () => document.removeEventListener("visibilitychange", onChange);
  },
  restoreLastConversationOnOpen: true,
  forceProjectScopeOnOpen: true,
  autoSelectFallbackOnRemoval: true,
};

type PanelAnchor = { top: number; end: number };

/**
 * The store behind the anchor is the document itself (the header Chats icon's box). useSyncExternalStore
 * compares snapshots with Object.is and re-reads after every commit, so this reader MUST hand back the
 * SAME object while the measurement is unchanged — a fresh `{ top, end }` per call re-renders forever.
 * The cache can be module-scope because the measurement is global: one document, one header icon.
 *
 * ⚠️ BUG: this reader measures LIVE layout, but `subscribe` below only announces window resize, so the
 * anchor is neither stable nor live — it is recomputed during EVERY render of PortalChatShell (which is
 * not memoised and re-renders on any usePortalChatCore change: inbound socket message, receipt, selected
 * conversation, `connected` flip). Concrete failure on /dashboard: `onReduceHeader` starts true
 * (reducers/portalDashboard.ts initialState) so portalHeader is `h-48` (192px); scrolling #portalDiv past
 * 10px dispatches setReduceHeader(false) → `h-18` (72px). The open panel correctly does not move then (no
 * re-render), but the NEXT unrelated chat event re-renders the Shell, re-measures, and teleports the panel
 * ~120px upward mid-read. Fix: measure only inside the subscribe callbacks into the module cache and make
 * this function a pure read of that cache (no querySelector/getBoundingClientRect in the render path).
 *
 * ⚠️ Reading live layout from getSnapshot also breaks React's store-consistency machinery: for
 * non-blocking lanes React re-invokes getSnapshot at the end of a concurrent render and throws the
 * completed render away if the value changed. Any layout change anywhere in the app while the panel is
 * open (image load, widget resize, the header class swap) can therefore invalidate unrelated
 * `startTransition` work, and each commit costs two forced synchronous reflows (render + post-commit
 * check) plus a document-wide querySelector.
 */
let anchorSnapshot: PanelAnchor | null = null;

function readPanelAnchor(): PanelAnchor | null {
  const icon = document.querySelector<HTMLElement>('[data-testid="portalChatIcon"]');
  if (!icon) {
    // ⚠️ BUG: a TRANSIENT absence of the icon discards the last good anchor, so the open panel snaps from
    // its inline top/insetInlineEnd to the `inset-e-3.5 top-22` corner. Two reachable triggers: routes with
    // `showHeader: false` make Router.tsx dispatch setRenderHeader(false) and unmount <Header/>; and on
    // /dashboard Header renders portalHeaderShimmer() (no <PortalChatIcon/>) whenever isDataLoading().
    // Coming back re-mounts the icon but nothing notifies the store, so the panel stays in the corner until
    // some unrelated re-render happens. Fix: keep the previous measurement instead of nulling the cache.
    anchorSnapshot = null;
    return null;
  }
  const rect = icon.getBoundingClientRect();
  const rtl = document.documentElement.dir === "rtl";
  const top = rect.bottom + PANEL_ANCHOR_GAP;
  const end = rtl ? rect.left : window.innerWidth - rect.right;
  if (anchorSnapshot === null || !Object.is(anchorSnapshot.top, top) || !Object.is(anchorSnapshot.end, end)) {
    anchorSnapshot = { top, end };
  }
  return anchorSnapshot;
}

function readNoAnchor(): PanelAnchor | null {
  return null;
}

/**
 * ⚠️ `resize` is NOT what moves the anchor. The icon's box changes on header height changes (the dashboard
 * setReduceHeader collapse), on scroll of the `overflow-auto` main column that contains the Header, and on
 * the icon mounting/unmounting — none of which fire `resize`, so onStoreChange never announces them. A real
 * store here needs a ResizeObserver on the icon/header plus the scroll container, not just this listener.
 */
function subscribeToResize(onStoreChange: () => void): () => void {
  window.addEventListener("resize", onStoreChange);
  return () => window.removeEventListener("resize", onStoreChange);
}

function subscribeToNothing(): () => void {
  return () => undefined;
}

/**
 * The panel opens as an overlay under the header Chats icon, its END edge in line with the icon
 * (the Notifications pattern); if the header is not rendered the panel falls back to its fixed
 * corner placement. Both arguments are module-scope constants, so flipping `enabled` is the only
 * thing that can re-subscribe.
 */
function usePanelAnchor(enabled: boolean): PanelAnchor | null {
  return useSyncExternalStore(
    enabled ? subscribeToResize : subscribeToNothing,
    enabled ? readPanelAnchor : readNoAnchor,
  );
}

/** The two panes plus the ONE list model they share (see listModelContext). Lives under the provider. */
function PortalChatShell({
  connected,
  maximized,
  onToggleMaximize,
  onClose,
}: {
  readonly connected: boolean;
  readonly maximized: boolean;
  readonly onToggleMaximize: () => void;
  readonly onClose: () => void;
}) {
  const listModel = useChatListModel();
  const anchor = usePanelAnchor(!maximized);
  const anchored = !maximized && anchor !== null;

  return (
    <ChatListModelContext.Provider value={listModel}>
      <div
        data-testid="portalChatPanel"
        className={`fixed z-1200 flex overflow-hidden border border-[#EEF2F5] bg-white shadow-[0_1rem_2.5rem_rgba(28,34,40,0.14)] ${
          maximized
            ? "inset-x-0 bottom-0 top-[4.7rem] rounded-none"
            : `bottom-3.5 w-261.5 max-w-[calc(100vw-1.75rem)] rounded-xl ${anchored ? "" : "inset-e-3.5 top-22"}`
        }`}
        style={!maximized && anchor ? { top: anchor.top, insetInlineEnd: anchor.end } : undefined}
      >
        <ChatListPane
          connected={connected}
          maximized={maximized}
          onToggleMaximize={onToggleMaximize}
          onClose={onClose}
        />
        <ChatThreadPane />
      </div>
    </ChatListModelContext.Provider>
  );
}

/**
 * Portal Chat — the overlay panel: anchored under the header Chats icon, maximize to full-bleed
 * below the header, close resets maximize. Mounted once at Router level (like the Notification
 * panel) so it survives route changes. All chat STATE lives in the shared core
 * (../core/usePortalChatCore) — this file is web chrome.
 */
function PortalChatPanel() {
  const dispatch = useDispatch();
  const show = useSelector((state: RootState) => state.showPortalChat);
  const userDetails = useSelector((state: RootState) => state.userDetails);
  const userId = String(userDetails._id ?? userDetails.id ?? "");
  const userName = `${userDetails.firstName ?? ""} ${userDetails.lastName ?? ""}`.trim();

  const [maximized, setMaximized] = useState(false);

  const { pathname } = useLocation();
  const currentProjectId = useMemo(() => {
    if (!show) return null;
    const match = /\/project\/([0-9a-fA-F]{24})/.exec(pathname);
    return match?.[1] ?? null;
  }, [show, pathname]);

  const { contextValue, connected, dialogs, bumpMenuEpoch } = usePortalChatCore({
    show,
    userId,
    userName,
    currentProjectId,
    platform: webPlatform,
  });

  const close = () => {
    dispatch(setShowPortalChat(false));
    setMaximized(false); // PRD: closing resets maximize
  };

  const toggleMaximize = () => {
    setMaximized((prev) => !prev);
    bumpMenuEpoch(); // PRD §4.6: open menus close on maximise/restore
  };

  if (!show || !userId) return null;

  return (
    <PortalChatContext.Provider value={contextValue}>
      <PortalChatShell connected={connected} maximized={maximized} onToggleMaximize={toggleMaximize} onClose={close} />
      {dialogs.forwardMessage && <ForwardDialog message={dialogs.forwardMessage} onClose={dialogs.closeForward} />}
      {dialogs.receiptsMessage && <ReceiptsDialog message={dialogs.receiptsMessage} onClose={dialogs.closeReceipts} />}
      {dialogs.reactionsMessage && (
        <ReactionsDialog message={dialogs.reactionsMessage} onClose={dialogs.closeReactions} />
      )}
      {dialogs.editDialogMessage && (
        <EditMessageDialog message={dialogs.editDialogMessage} onClose={dialogs.closeEdit} />
      )}
    </PortalChatContext.Provider>
  );
}

export default PortalChatPanel;
